import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider
} from '@tanstack/react-query'
import { useToast } from '@veracity/vui'
import { AxiosError } from 'axios'
import { PropsWithChildren, useMemo } from 'react'

import { config } from '@/config'

type ErrorResponse =
  | { title: string }
  | { message: string }
  | { Message: string }
  | string

export const ReactQueryProvider = ({ children }: PropsWithChildren) => {
  const { showError } = useToast()

  const errorHandling = (err: AxiosError) => {
    const errorContent = err.response?.data as ErrorResponse
    switch (err.response?.status) {
      case 400:
        if (typeof errorContent === 'string') showError(errorContent)
        else if ('message' in errorContent) showError(errorContent.message)
        else if ('Message' in errorContent) showError(errorContent.Message)
        else if ('title' in errorContent) showError(errorContent.title)
        else showError('Something went wrong.')
        break
      case 401:
        if (!['/about', '/'].includes(window.location.pathname)) {
          window.location.replace(
            `/account/signIn?redirectUrl=${window.location.pathname}`
          )
        }
        break
      case 403:
        showError('You do not have permission to perform this action.')
        break
      case 404:
        if (typeof errorContent === 'string' && !errorContent.startsWith('<'))
          showError(errorContent)
        else if (typeof errorContent !== 'string' && 'message' in errorContent)
          showError(errorContent.message)
        else showError("The requested resource doesn't exist.")
        break
      case 500:
      default:
        showError(
          'Something went wrong. Please contact hero_support@dnv.com with as many details as possible to reproduce the issue.',
          'slow'
        )
        break
    }
  }

  const queryClient = useMemo(() => {
    return new QueryClient({
      queryCache: new QueryCache({
        onError: error => errorHandling(error as AxiosError)
      }),
      mutationCache: new MutationCache({
        onError: error => errorHandling(error as AxiosError)
      }),
      defaultOptions: {
        queries: {
          retry: false,
          staleTime: config.requestsConfig.staleTime.default
        },
        mutations: {
          retry: false
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  )
}
